import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { telegramAdminService } from '@/services/telegram-admin-service';

export function TelegramBotConfiguration() {
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSetupWebhook = async () => {
    setIsSettingUp(true);
    try {
      const result = await telegramAdminService.setupWebhook();
      toast.success('Webhook setup successfully!');
      setWebhookInfo(result.webhookInfo);
    } catch (error: any) {
      toast.error(`Failed to setup webhook: ${error.message}`);
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleVerifyWebhook = async () => {
    setIsLoading(true);
    try {
      const info = await telegramAdminService.getWebhookInfo();
      setWebhookInfo(info);
      toast.success('Webhook info refreshed');
    } catch (error: any) {
      toast.error(`Failed to get webhook info: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🤖 Telegram Bot Configuration
          </CardTitle>
          <CardDescription>
            Setup and manage your Telegram bot webhook connection
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
            <div>
              <p className="font-medium">Bot Status</p>
              <p className="text-sm text-muted-foreground">
                {webhookInfo ? 'Webhook configured' : 'Not configured'}
              </p>
            </div>
            {webhookInfo ? (
              <Badge variant="default" className="gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Online
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1">
                <XCircle className="h-3 w-3" />
                Offline
              </Badge>
            )}
          </div>

          {webhookInfo && (
            <div className="space-y-3 p-4 border rounded-lg">
              <h4 className="font-medium">Webhook Information</h4>
              <div className="grid gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">URL:</span>
                  <span className="font-mono text-xs break-all ml-2">
                    {webhookInfo.url}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pending Updates:</span>
                  <span>{webhookInfo.pending_update_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Has Custom Certificate:</span>
                  <span>{webhookInfo.has_custom_certificate ? 'Yes' : 'No'}</span>
                </div>
                {webhookInfo.last_error_message && (
                  <div className="flex justify-between text-destructive">
                    <span>Last Error:</span>
                    <span className="ml-2">{webhookInfo.last_error_message}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleSetupWebhook}
              disabled={isSettingUp}
            >
              {isSettingUp && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Setup Webhook
            </Button>
            <Button
              variant="outline"
              onClick={handleVerifyWebhook}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Verify Webhook
            </Button>
          </div>

          <div className="p-4 border border-yellow-500/50 rounded-lg bg-yellow-500/10">
            <p className="text-sm font-medium">⚠️ Important Notes:</p>
            <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
              <li>Bot Token and Webhook Secret must be set in Supabase Secrets</li>
              <li>Setup webhook only needs to be done once</li>
              <li>You can verify webhook status anytime without re-setup</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
